import { CustomerType, SortOrderType } from '@repo/common';
import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@repo/shadcn';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { ChevronDownIcon, Edit, Loader2Icon, Trash } from 'lucide-react';
import { LuArrowUpDown } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { ActiveStatusBadge } from '@components/ui/badge/active-status-badge.component';
import { ButtonGroup } from '@components/ui/button';

import { CustomerTypeBadge } from '@/components/ui/badge';
import { Route } from '@/constants';
import { TCustomerListItem } from '@/features/slice/customers/customer.schema';
import { useUrlFilter } from '@/hooks';
import { useCustomerList } from '@/modules/customers/customer-list/customer-list.hook';

const ActionCell = ({ row }: CellContext<TCustomerListItem, unknown>) => {
    const {
        handleDeleteCustomer,
        isDeleting,
        handleEditCustomer,
        handleSuspendCustomer,
        handleActivateCustomer,
    } = useCustomerList();

    const renderSuspendActivate = () => {
        if (row.original.customer_type !== CustomerType.WHOLESALE) {
            return null;
        }

        if (row.original.status === 'active') {
            return (
                <DropdownMenuItem
                    className={'text-warning flex gap-2'}
                    onClick={() => handleSuspendCustomer(row.original)}
                >
                    <Trash className={'size-4'} />
                    Suspend
                </DropdownMenuItem>
            );
        } else {
            return (
                <DropdownMenuItem
                    className={'text-success flex gap-2'}
                    onClick={() => handleActivateCustomer(row.original)}
                >
                    <Edit className={'size-4'} />
                    Activate
                </DropdownMenuItem>
            );
        }
    };

    return (
        <div className={'flex justify-end'}>
            <ButtonGroup>
                <Link to={`${Route.CUSTOMERS}/${row.original.id}`}>
                    <Button type={'button'} variant={'outline'}>
                        View Details
                    </Button>
                </Link>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="!pl-2">
                            <ChevronDownIcon />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="[--radius:1rem]"
                    >
                        <DropdownMenuGroup>
                            <DropdownMenuItem
                                className={'flex gap-2'}
                                onClick={() =>
                                    handleEditCustomer(row.original.id)
                                }
                            >
                                <Edit className={'size-4'} />
                                Edit
                            </DropdownMenuItem>

                            {renderSuspendActivate()}

                            <DropdownMenuItem
                                className={'flex gap-2 text-destructive'}
                                onClick={() =>
                                    handleDeleteCustomer(row.original.id)
                                }
                            >
                                {isDeleting ? (
                                    <Loader2Icon className={'animate-spin'} />
                                ) : (
                                    <>
                                        <Trash className={'size-4'} />
                                        Delete
                                    </>
                                )}
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </ButtonGroup>
        </div>
    );
};

const NameHeader = () => {
    const { sortOrder, setSortOrder, setSortBy, sortBy } = useUrlFilter();

    const handleToggleSort = () => {
        if (sortOrder === SortOrderType.ASC) {
            setSortOrder(SortOrderType.DESC);
        } else if (sortOrder === SortOrderType.DESC) {
            setSortOrder(SortOrderType.ASC);
        }

        if (sortBy !== 'name') {
            setSortBy('name');
        }
    };

    return (
        <Button variant="ghost" onClick={handleToggleSort}>
            Customer Name
            <LuArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
    );
};

export const customerListColumns: ColumnDef<any>[] = [
    {
        accessorKey: 'name',
        header: NameHeader,
        cell: ({ row }) => {
            const customerType = row.original.customer_type;
            const customerName = String(row.getValue('name') ?? '');
            let customerDetailsPath = `${Route.CUSTOMERS}/${row.original.id}`;

            if (customerType === CustomerType.WHOLESALE) {
                customerDetailsPath = `${Route.WHOLESALE_ORDER_CUSTOMERS}/${row.original.id}`;
            }
            return (
                <div className="max-w-[180px] truncate font-medium">
                    <Link to={customerDetailsPath} title={customerName}>
                        {customerName}
                    </Link>
                </div>
            );
        },
    },

    {
        accessorKey: 'email',
        header: () => <div>Email Address</div>,
        cell: ({ row }) => {
            const email = String(row.getValue('email') ?? '');
            return (
                <div
                    className="max-w-[220px] truncate font-medium"
                    title={email}
                >
                    {email}
                </div>
            );
        },
    },
    {
        accessorKey: 'phone_number',
        header: () => <div>Phone Number</div>,
        cell: ({ row }) => (
            <div className="font-medium">{row.getValue('phone_number')}</div>
        ),
    },
    {
        accessorKey: 'address',
        header: () => <div>Address</div>,
        cell: ({ row }) => {
            const address = String(row.getValue('address') ?? '');
            return (
                <div
                    className="max-w-[240px] truncate font-medium capitalize"
                    title={address}
                >
                    {address}
                </div>
            );
        },
    },
    {
        accessorKey: 'customer_type',
        header: () => <div>Customer Type</div>,
        cell: ({ row }) => (
            <CustomerTypeBadge value={row.getValue('customer_type')} />
        ),
    },

    {
        accessorKey: 'status',
        header: () => <div>Status</div>,
        cell: ({ row }) => (
            <ActiveStatusBadge isActive={row.original.status === 'active'} />
        ),
    },

    {
        accessorKey: 'actions',
        header: () => <div className={'text-right'}>Actions</div>,
        cell: ActionCell,
    },
];
